import {
  AlertTriangleIcon,
  ClockIcon,
  ScaleIcon,
  SproutIcon,
  ClipboardListIcon,
  MessageCircleIcon,
  FileTextIcon,
  TableIcon,
  BabyIcon,
  GitBranchIcon,
  CameraIcon,
} from 'lucide-react';

export const CHECKOUT_URL = 'https://pediatricortho-en.impultienda.ar/checkout/';

export const IMAGES = {
  mockup: 'https://cdn.magicpatterns.com/uploads/miUBDhxybyMtcYCkrCgGTh/MOCKUP_PEDIATRICORTHO-EN.png',
  author: 'https://cdn.magicpatterns.com/uploads/k21Kq1WvsUdssdfD7KkkCM/VALERIA_MONTES_-_BIOMECANICA.png',
};

export const beforeItems = [
  'You may feel uncertain about which findings deserve further investigation.',
  'You spend valuable time searching through scattered notes and references.',
  'You struggle to organize the information needed to compare treatment alternatives.',
  'You worry about overlooking important factors related to growth, eruption, or occlusion.',
  'You want to improve your clinical reasoning but need a more practical way to organize what you know.',
  'You find it difficult to explain the rationale behind different treatment options.',
];

export const afterItems = [
  'Organize your clinical findings using a structured evaluation framework.',
  'Identify the diagnostic questions that deserve further attention.',
  'Consider growth, dentition stage, occlusion, and other relevant clinical factors.',
  'Compare potential treatment approaches according to their indications and limitations.',
  'Document your reasoning and identify situations requiring further assessment or specialist referral.',
  'Explain your clinical reasoning more clearly when discussing treatment options with families.',
];

export const painPoints = [
  {
    icon: AlertTriangleIcon,
    title: "You're evaluating a child with a developing malocclusion and aren't sure which findings to prioritize.",
    body: 'You recognize the problem, but you want a more structured way to organize your assessment before considering treatment alternatives.',
  },
  {
    icon: ClockIcon,
    title: 'You spend too much time searching through different references.',
    body: 'You want the relevant clinical considerations organized in one place instead of repeatedly looking through books, notes, and scattered resources.',
  },
  {
    icon: ScaleIcon,
    title: 'You hesitate when comparing different orthodontic appliances.',
    body: 'You understand that different appliances serve different purposes, but you want a clearer framework for reviewing their indications, limitations, and clinical requirements.',
  },
  {
    icon: SproutIcon,
    title: "You're unsure how growth and the stage of dentition should influence your reasoning.",
    body: 'You want to understand which developmental factors deserve attention before making decisions about interceptive treatment.',
  },
  {
    icon: ClipboardListIcon,
    title: 'You want to improve the consistency of your clinical evaluations.',
    body: 'You need practical reference materials that help you organize your findings, document your reasoning, and identify questions that still need to be answered.',
  },
  {
    icon: MessageCircleIcon,
    title: 'You want to feel better prepared before discussing a treatment plan with parents.',
    body: "You want to explain what you've observed, why additional evaluation may be necessary, and how different treatment options may be considered.",
  },
];

export const volumes = [
  {
    numeral: 'I',
    title: 'The Pediatric Orthodontic Case Assessment Framework',
    tagline: 'Build a structured understanding of the case before considering treatment alternatives.',
    description:
      "I'll guide you through the main components of a pediatric orthodontic assessment, including medical and dental history, clinical examination, dentition stage, occlusal findings, facial considerations, and the identification of information that may require further investigation.",
    bullets: [
      'A structured approach to collecting relevant patient information.',
      'Key elements of the clinical examination.',
      'Considerations related to growth and dental development.',
      'Methods for organizing findings into a clinical summary.',
      'Questions to address before establishing a diagnostic conclusion.',
    ],
    closing:
      'The goal is to help you approach each case systematically instead of allowing isolated findings to dictate your entire evaluation.',
  },
  {
    numeral: 'II',
    title: 'Mixed Dentition Analysis & Developmental Assessment',
    tagline: 'Understand the developmental factors that can influence your clinical reasoning.',
    description:
      "The mixed dentition stage presents specific assessment challenges. In this volume, I'll help you organize the information needed to evaluate eruption patterns, available space, dental relationships, and developmental changes.",
    bullets: [
      'The clinical significance of the mixed dentition stage.',
      'Eruption patterns and developmental considerations.',
      'Space analysis concepts and their limitations.',
      'Findings that may justify additional diagnostic investigation.',
      'How to document developmental observations clearly.',
    ],
    closing:
      "You'll have a more organized reference for reviewing developmental factors before considering possible treatment approaches.",
  },
  {
    numeral: 'III',
    title: 'Malocclusion Recognition & Diagnostic Reasoning',
    tagline: 'Learn to organize your findings before comparing possible treatment options.',
    description:
      "Recognizing a malocclusion is only one part of the process. Understanding the nature of the problem and identifying the information needed to evaluate it are equally important. I'll walk you through educational frameworks for reviewing common pediatric orthodontic findings.",
    bullets: [
      'Anterior and posterior crossbite assessment.',
      'Selected sagittal, vertical, and transverse occlusal relationships.',
      'Dental and skeletal considerations in diagnostic reasoning.',
      'The importance of distinguishing clinical findings from diagnostic conclusions.',
      'Factors that may indicate the need for additional records or specialist assessment.',
    ],
    closing:
      'The objective is to strengthen the structure of your reasoning, not to replace a comprehensive diagnosis or individualized clinical judgment.',
  },
  {
    numeral: 'IV',
    title: 'Orthodontic Appliance Selection & Treatment Comparison',
    tagline: 'Compare potential appliances according to their purpose, indications, and limitations.',
    description:
      "Choosing an appliance should begin with understanding the problem you are trying to address. That's why I've dedicated an entire volume to organizing the considerations involved in comparing different orthodontic appliances.",
    bullets: [
      'Functional differences among common appliance categories.',
      'Clinical objectives associated with different appliances.',
      'Factors influencing appliance selection.',
      'Patient-related considerations, including age, development, and cooperation.',
      'Contraindications, limitations, and situations requiring further assessment.',
      'Questions to review before determining whether an appliance may be appropriate.',
    ],
    closing:
      "I'll help you build a more structured way to compare alternatives without treating any appliance as a universal solution.",
  },
  {
    numeral: 'V',
    title: 'Clinical Decision Pathways for Interceptive Orthodontics',
    tagline: 'Organize the steps between identifying a clinical problem and determining the next appropriate action.',
    description:
      'Each pathway will help you organize the clinical questions, relevant findings, possible alternatives, and follow-up considerations associated with a particular type of presentation.',
    bullets: [
      'Evaluating findings associated with crossbites.',
      'Reviewing eruption and space-related concerns.',
      'Organizing considerations for selected developing malocclusions.',
      'Identifying information gaps before proceeding.',
      'Recognizing circumstances that may require specialist referral.',
      'Documenting the rationale behind a clinical decision.',
    ],
    closing:
      'These pathways are intended to support clinical reasoning rather than provide automatic diagnoses or treatment prescriptions.',
  },
  {
    numeral: 'VI',
    title: 'Treatment Planning, Follow-Up & Clinical Documentation',
    tagline: 'Turn your clinical reasoning into a more organized plan for evaluation and follow-up.',
    description:
      "A structured decision-making process doesn't end when you identify a possible treatment approach. You also need to document your findings, communicate your reasoning, and establish an appropriate process for reviewing the patient's progress.",
    bullets: [
      'Structuring the rationale behind a treatment plan.',
      'Documenting the objectives of an intervention.',
      'Establishing follow-up considerations.',
      'Reviewing treatment response and unexpected changes.',
      'Recognizing factors that may require reassessment.',
      'Organizing clinical discussions with parents or caregivers.',
    ],
    closing:
      'My goal is to give you a practical reference that supports a more consistent approach to clinical documentation and case review.',
  },
];

export const libraryItems = [
  {
    icon: FileTextIcon,
    title: 'Structured Case Assessment Frameworks',
    body: 'Organize the information you collect during an evaluation and identify the clinical questions that still need to be addressed.',
  },
  {
    icon: TableIcon,
    title: 'Appliance Comparison Tables',
    body: 'Review the objectives, indications, limitations, and patient-related considerations associated with different appliance categories.',
  },
  {
    icon: BabyIcon,
    title: 'Developmental Assessment References',
    body: 'Explore the role of dentition stage, eruption, growth, and space-related findings in pediatric orthodontic assessment.',
  },
  {
    icon: GitBranchIcon,
    title: 'Clinical Decision Pathways',
    body: 'Follow structured educational examples that demonstrate how different findings can lead to different diagnostic questions and treatment considerations.',
  },
  {
    icon: CameraIcon,
    title: 'Case Documentation Tools',
    body: 'Use practical templates to organize clinical observations, document your reasoning, and prepare for follow-up evaluations.',
  },
];

export const bonuses = [
  {
    number: 1,
    image: 'https://cdn.magicpatterns.com/uploads/sa3E3PMCMrcpQ46DY38x3P/BONUS_1_PEIDATRICORTHO-EN.png',
    title: 'The Pediatric Orthodontic Case Study Collection',
    tagline: 'A collection of educational cases for practicing clinical reasoning.',
    body: 'Work through illustrative pediatric orthodontic scenarios involving different developmental stages and occlusal findings. Each case includes the presentation, relevant clinical observations, questions to consider, potential diagnostic considerations, and an explanation of the reasoning process.',
    value: 19,
  },
  {
    number: 2,
    image: 'https://cdn.magicpatterns.com/uploads/cgWTkzFb4mB11fk6xPVmRf/BONUS_2_PEIDTRICORTHO-EN.png',
    title: 'The Mixed Dentition Assessment Worksheets',
    tagline: 'Practical worksheets for organizing developmental findings.',
    body: 'Record relevant observations about eruption, dentition stage, available space, and other factors that may influence your assessment. Designed to help you organize information consistently and identify areas that may require further investigation.',
    value: 15,
  },
  {
    number: 3,
    image: 'https://cdn.magicpatterns.com/uploads/qzxfCufhDvaWgdFiedhcyK/BONUS_3_PEIDATRICORTHO-EN.png',
    title: 'The Orthodontic Appliance Comparison Atlas',
    tagline: 'A visual reference for comparing common appliance categories.',
    body: 'Comparison tables covering the general purpose, clinical objectives, relevant indications, limitations, and patient-related considerations associated with different appliance types — to support your review of treatment alternatives.',
    value: 19,
  },
  {
    number: 4,
    image: 'https://cdn.magicpatterns.com/uploads/1PcFBA8BV1rtJjHgqL6aTd/BONUS_4_PEDIATRICORTHO-EN.png',
    title: 'The Pediatric Orthodontic Clinical Red Flags Checklist',
    tagline: 'A structured checklist for identifying findings that deserve closer attention.',
    body: 'Review selected clinical findings related to occlusion, eruption, and development. Organize questions for further evaluation and recognize situations in which additional investigation or specialist referral may be appropriate.',
    value: 12,
  },
  {
    number: 5,
    image: 'https://cdn.magicpatterns.com/uploads/foSnYgoqFQLBBfuwA1tBV6/BONUS_5_PEDIATRICORTHO-EN.png',
    title: 'The Orthodontic Follow-Up & Case Documentation Kit',
    tagline: 'Practical templates for documenting your clinical reasoning and monitoring progress.',
    body: 'Structured forms for recording initial findings, treatment objectives, follow-up observations, changes in the clinical presentation, and questions that may require reassessment. Adapt them to your professional workflow.',
    value: 15,
  },
];

export const offerChecks = [
  'Instant digital access',
  'All 6 specialized volumes',
  'All 5 clinical bonuses',
  'Downloadable digital materials',
  'Lifetime access to the purchased materials',
  'No monthly subscription',
];

export const guaranteeSteps = [
  'Explore all six volumes.',
  'Review the appliance comparison resources.',
  'Work through the educational case studies.',
  'Examine the assessment worksheets.',
  'Review the documentation and follow-up tools.',
];

export const faqs = [
  {
    q: 'How will I receive the material?',
    a: "After your payment is confirmed, you'll receive instructions for accessing your digital purchase. The six volumes and five bonuses will be provided in the formats specified on the checkout page.",
  },
  {
    q: 'Is this resource suitable for general dentists?',
    a: 'The material is designed for dental professionals who want to deepen their understanding of pediatric orthodontic assessment and clinical decision-making. Your existing training and scope of practice will determine how you apply the information.',
  },
  {
    q: 'Do I need to be an orthodontic specialist?',
    a: "You don't necessarily need to be an orthodontic specialist to study the material. However, it is a professional educational resource intended for people with appropriate dental training, not a beginner's guide for the general public.",
  },
  {
    q: 'Does the blueprint tell me exactly which appliance to use for every patient?',
    a: 'No. Every patient requires an individualized assessment. The blueprint helps you organize clinical findings, understand general appliance considerations, and compare potential alternatives. It does not replace comprehensive diagnosis, professional judgment, or specialist consultation when indicated.',
  },
  {
    q: 'Is this a clinical certification course?',
    a: 'No. This is a digital educational reference collection. Purchasing it does not provide a professional license, specialist qualification, or clinical certification.',
  },
  {
    q: 'Can I download the files?',
    a: 'The collection is intended to be delivered as downloadable digital material. Please review the checkout details for the exact file formats and access instructions.',
  },
  {
    q: 'Is this a recurring subscription?',
    a: 'No. The advertised price is a one-time payment of $27 USD. There are no recurring subscription charges for this offer.',
  },
  {
    q: 'Will the information apply in my country?',
    a: 'The educational principles may be useful to dental professionals in different countries, but clinical regulations, professional scope, documentation requirements, and available treatment options vary. Always follow applicable local requirements and current evidence.',
  },
  {
    q: "What if the material isn't right for me?",
    a: 'The offer includes a 7-day money-back guarantee, provided the published refund terms are followed. Contact the support team within the applicable period to request a refund.',
  },
];

export const recentBuyers = [
  { name: 'María Fernanda', country: 'Mexico' },
  { name: 'Carlos Andrés', country: 'Colombia' },
  { name: 'Lucía', country: 'Argentina' },
  { name: 'José Luis', country: 'Peru' },
  { name: 'Valentina', country: 'Chile' },
  { name: 'Diego', country: 'Spain' },
  { name: 'Camila', country: 'Ecuador' },
  { name: 'Andrés', country: 'Venezuela' },
  { name: 'Gabriela', country: 'Guatemala' },
  { name: 'Ricardo', country: 'Dominican Republic' },
  { name: 'Daniela', country: 'Costa Rica' },
  { name: 'Sebastián', country: 'Uruguay' },
  { name: 'Paola', country: 'Bolivia' },
  { name: 'Fernando', country: 'Paraguay' },
  { name: 'Mariana', country: 'Panama' },
];
